import React, { useEffect, useMemo, useRef, useState } from 'react'

import { NOTIFICATION_SETTINGS } from 'constants/notifications'

import { useSnackbar } from 'contexts'
import { useUserProfileQuery, useUserProfileMutations } from 'hooks'

import { useTheme, Typography, Stack, Divider, Tooltip, Box } from '@mui/material'

import { Switch, Skeleton, hideScrollbar } from '@repo/ui'
import { SettingsSection } from 'components/display'
import { vhMinus } from 'utils/textZoom'

type NotificationChannel = 'platform' | 'email' | 'sms'
// ⚠️ REVERSED 2026-09-03 (VRG-560), by user decision. `sms` is still OPTIONAL,
// but an absent key now means ON for platform/staff — this surface is opt-OUT.
//
// It previously read: "`sms` is optional and opt-in — an absent key means off.
// Never default it to true (VERA-291 slice 3, batch 4)." That was a considered
// decision at the time, not an oversight, and working-conventions §65 recorded
// the contradiction between it and the opt-out directive as OPEN. The user
// settled it: staff SMS is opt-out.
//
// The reasoning is the recipient relationship, and it is the reason the
// OWNER-facing page (apps/app) is NOT changing: a staff member has a known
// business relationship with Vera and gave their number in an operational
// context, so the costly failure is MISSING an alert nobody opted them into.
// For a consumer the costly failure is the opposite one, so consumers stay
// opt-in. Two surfaces, two defaults, on purpose.
//
// SMS_STAFF_OPT_OUT_DEFAULT below is the single place that default is
// expressed; it must stay in step with `resolveStaffSmsRecipient` in the API
// (`notification.sendgrid.service.ts`), which suppresses only on an explicit
// `sms === false`.
type NotificationPrefs = Record<string, { platform: boolean; email: boolean; sms?: boolean }>

// The staff SMS default (see the note above). Read wherever an absent `sms`
// key has to be resolved to a switch position, so the UI shows what the
// backend will actually do rather than a second, independently-typed guess.
const SMS_STAFF_OPT_OUT_DEFAULT = true

// Categories whose Email channel is mandatory — these carry transactional / critical
// mail (auth, verification, security, billing, account activity) that always sends
// regardless of preference (see API notification-category.classification.ts). The Email
// toggle is locked ON for them so the UI matches the backend's always-send behavior.
// SMS is NEVER forced by this set. It is opt-OUT for staff (VRG-560, above), which
// means ON by default — but unlike Email it stays switchable off, including for
// these categories. The backend agrees: `transactional: true` makes the EMAIL
// non-negotiable and deliberately does NOT override an explicit SMS opt-out, since
// email already guarantees delivery and forcing a duplicate text secures nothing.
const REQUIRED_EMAIL = new Set(['system', 'security', 'account', 'billing'])

// Fixed column width so the Email/SMS header labels line up with the switches
// beneath them (both the header Stack and the per-row Stack use the same spacing).
const CHANNEL_COLUMN_WIDTH = 42

// VERA-360 default channels for a new user: in-app (platform) + email on for every
// category EXCEPT Promotional, which is opt-in (both off). Required categories' email
// stays on (and is locked in the UI).
const buildDefaultPrefs = (): NotificationPrefs =>
  Object.keys(NOTIFICATION_SETTINGS).reduce((acc, key) => {
    const optInOnly = key === 'promotional'
    // VRG-560: `sms` is now seeded explicitly rather than left absent, so the
    // switch position and the persisted value agree from the first render.
    //
    // Promotional stays OFF on every channel, and that carve-out is the one
    // place opt-out must not reach: marketing texts are the case consent law
    // is actually about, and a default-on promotional SMS is a different kind
    // of mistake from a missed operational alert. The opt-out default is for
    // operational mail — security alerts, stock-outs — not for marketing.
    acc[key] = {
      platform: !optInOnly,
      email: !optInOnly,
      sms: optInOnly ? false : SMS_STAFF_OPT_OUT_DEFAULT,
    }
    return acc
  }, {} as NotificationPrefs)

export const NotificationsPage = () => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()

  // Source the profile from the admin-profile query (GET /auth/admin/profile),
  // which carries notification_settings — useAuth()'s WorkOS-mapped profile does not.
  const { data: profile, isLoading } = useUserProfileQuery()
  const { updateAsync } = useUserProfileMutations()

  // `profile` from useUserProfileQuery is a fresh object every render (it's mapped on
  // read), so depend on the SERIALIZED notification_settings — a stable primitive that
  // only changes when the content actually changes. Depending on `profile` directly
  // recomputes initialPrefs every render → the sync effect below would setState every
  // render → "Maximum update depth exceeded" loop.
  const savedKey = JSON.stringify(profile?.notification_settings ?? null)

  // Seed every known category from defaults, then overlay whatever the profile has.
  //
  // VRG-560: the overlay is now PER CATEGORY, not a single top-level spread, and
  // that is load-bearing rather than tidying. A top-level spread replaces a
  // category object wholesale, so a profile saved before the SMS column existed
  // (`{ platform, email }`, no `sms`) wiped the seeded default and left `sms`
  // undefined — every such switch would render off while the backend, which
  // suppresses only on an explicit `false`, would send. The UI would have been
  // lying about what happens, for exactly the users who never opened this page.
  // Merging per category resolves the absent key to that category's own default,
  // so promotional stays off and the rest read on.
  const initialPrefs = useMemo<NotificationPrefs>(() => {
    const saved = profile?.notification_settings
    const defaults = buildDefaultPrefs()
    if (!saved || Object.keys(saved).length === 0) return defaults

    return Object.entries(saved).reduce<NotificationPrefs>(
      (acc, [key, value]) => {
        // A saved key with no seeded counterpart (a category retired from
        // NOTIFICATION_SETTINGS, or one added by another surface) still gets a
        // complete row rather than an undefined channel.
        const base = defaults[key] ?? {
          platform: true,
          email: true,
          sms: SMS_STAFF_OPT_OUT_DEFAULT,
        }
        acc[key] = { ...base, ...value }
        return acc
      },
      { ...defaults },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey])

  const [prefs, setPrefs] = useState<NotificationPrefs>(initialPrefs)
  // Mirror the latest prefs in a ref so rapid successive toggles each compute
  // their payload from the freshest state (not a stale render closure).
  const prefsRef = useRef(prefs)

  // Keep local state + ref in sync when the profile (re)loads.
  useEffect(() => {
    setPrefs(initialPrefs)
    prefsRef.current = initialPrefs
  }, [initialPrefs])

  const handleToggle = (key: string, channel: NotificationChannel) => {
    // Email is mandatory for critical categories — ignore attempts to turn it off.
    if (channel === 'email' && REQUIRED_EMAIL.has(key)) return

    const previous = prefsRef.current
    const next: NotificationPrefs = {
      ...previous,
      [key]: { ...previous[key], [channel]: !previous[key][channel] },
    }

    // Optimistic, per-switch update — no global disable, so the row flips
    // instantly with no flash of the other toggles while the save is in flight.
    prefsRef.current = next
    setPrefs(next)

    updateAsync({ notification_settings: next }).catch(() => {
      prefsRef.current = previous
      setPrefs(previous) // revert on failure
      notification({
        type: 'error',
        title: 'Could not update notification setting',
      })
    })
  }

  if (isLoading || !profile) {
    // Footprint-parity skeleton (§19): mirrors the loaded layout — the panel, the title in
    // SettingsSection's head, the sticky Email/SMS column labels, then category rows (label
    // + description + two toggle columns) — so nothing shifts when the real toggles arrive.
    // The title is NOT part of the skeleton's sticky row any more, because it is no longer
    // part of the loaded one; a skeleton that mirrors the previous layout is worse than
    // none, since it guarantees the shift it exists to prevent.
    //
    // Re-measured (viewport 1711, panel x=563 w=1100). It was 29px short — panel 598 against the
    // loaded 627 — from two separate causes, both fixed here:
    //
    //   1. the header row was 27 tall against the loaded 39. Its cells are 14px BARS, while the
    //      loaded header cells are body-text line boxes of 26; the shared `pb: 3` can't make up the
    //      12px difference. The bars now sit in 26px-high boxes.
    //   2. it reserved SIX category rows against the loaded SEVEN (System, Account, Billing,
    //      Security, Content, Promotional, Features), and each row's content was 54 rather than 44
    //      (a `lines={2}` description against the loaded one-line one).
    //
    // The measured target: header 39, then a rows Stack of 530 = 24 (`pt: 6`) + 6x(61 + 16) + 44,
    // where a row ELEMENT is 61 = 44 content + 16 inner gap + 1 divider, the last row has no
    // divider (44), and the outer `spacing={4}` supplies the 16 between elements. Content 44 =
    // an 18px label line + the 8px gap + an 18px description line.
    return (
      <SettingsSection>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pb: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            {/* Mirrors the "Category" header cell in the loaded table — the bar in that cell's own
                26px line box, so this row is the loaded 39 rather than 27. */}
            <Box sx={{ height: 26, display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="rounded" width={72} height={14} />
            </Box>
            <Stack direction="row" spacing={12}>
              {[0, 1].map((j) => (
                <Box key={j} sx={{ height: 26, display: 'flex', alignItems: 'center' }}>
                  <Skeleton variant="rounded" width={CHANNEL_COLUMN_WIDTH} height={14} />
                </Box>
              ))}
            </Stack>
          </Stack>
          <Stack spacing={4} sx={{ pt: 6 }}>
            {/* SEVEN rows, matching the seven notification categories. */}
            {Array.from({ length: 7 }).map((_, i) => (
              <Stack key={i} spacing={4}>
                <Stack direction="row" spacing={4} alignItems="flex-start" justifyContent="space-between">
                  {/* 44 tall: label line + 8px gap + description line, each in its own 18px box —
                      the bars are 14, so without the boxes the row comes out short. */}
                  <Stack spacing={2} sx={{ flex: 1, maxWidth: 360 }}>
                    <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
                      <Skeleton variant="rounded" width={170} height={14} />
                    </Box>
                    <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
                      <Skeleton variant="rounded" width={i % 2 ? 300 : 260} height={14} />
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={12}>
                    {[0, 1].map((j) => (
                      <Stack key={j} sx={{ width: CHANNEL_COLUMN_WIDTH, alignItems: 'center' }}>
                        <Skeleton variant="rounded" width={34} height={20} />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
                {i !== 6 && <Divider />}
              </Stack>
            ))}
          </Stack>
      </SettingsSection>
    )
  }

  // VERA-291 slice 3, batch 4: SMS column added here (was NOTE(VERA-360 / TD-044) —
  // this was the marked anchor). Internal/platform users have no phone-verification
  // flow (unlike the app's owner-facing Notifications page), so the SMS switch is
  // always enabled — it just persists the preference.
  //
  // VRG-560: SMS is opt-OUT here (see the type note at the top of this file). The
  // no-verification-flow fact above is now load-bearing rather than incidental —
  // the API's staff SMS gate cannot require `phone_verified_at`, because nothing
  // on this surface can ever set it, so the bar is a non-empty profile phone
  // number. That is the known soft spot in the design, recorded here and in
  // `resolveStaffSmsRecipient` rather than left for someone to rediscover.
  // NO section title. This panel is a table, and its header row names the columns —
  // "Category" belongs inline with Email and SMS, not stacked above them as a second
  // heading for the same thing. The page title (from the nav label) already says
  // Notifications.
  return (
    <SettingsSection>
      {/* The table header, and it stays STICKY: while the list scrolls, what has to remain
          visible is which column a toggle belongs to. All three labels sit on one line, so
          "Category" reads as the heading of the left-hand column rather than as a title for
          the panel. */}
      <Stack
          direction="row"
          spacing={4}
          justifyContent="space-between"
          alignContent="center"
          sx={{
            position: 'sticky',
            pb: 3,
            top: 0,
            zIndex: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            // Transparent so the sticky header always renders as the panel surface in
            // both modes. (It previously replicated the card via background.default + a
            // dark-mode overlay, which drifted when the card color changed.)
            backgroundColor: 'transparent',
          }}
        >
          <Typography variant="body2" pb={2}>
            Category
          </Typography>
          {/* NOTE(VERA-1114): In-app (platform) channel column removed until the in-app
              notification center exists — the toggle had nothing behind it. The `platform`
              pref is still persisted; re-add the column when the center ships. */}
          <Stack direction="row" spacing={12}>
            <Typography variant="body2" pb={2} sx={{ width: CHANNEL_COLUMN_WIDTH, textAlign: 'center' }}>
              Email
            </Typography>
            <Typography variant="body2" pb={2} sx={{ width: CHANNEL_COLUMN_WIDTH, textAlign: 'center' }}>
              SMS
            </Typography>
          </Stack>
        </Stack>
        <Stack
          spacing={4}
          sx={{
            maxHeight: vhMinus(288),
            overflowY: 'auto',
            pt: 6,
            ...hideScrollbar,
          }}
        >
          {Object.keys(prefs).map((setting, index) => (
            <Stack key={setting} spacing={4}>
              <Stack direction="row" spacing={4} alignItems="flex-start" justifyContent="space-between">
                <Stack spacing={2}>
                  <Typography variant="body2">
                    {NOTIFICATION_SETTINGS[setting]?.title ?? setting}
                  </Typography>
                  <Typography variant="body3" color="text.secondary">
                    {NOTIFICATION_SETTINGS[setting]?.description}
                  </Typography>
                </Stack>
                <Stack spacing={12} direction="row">
                  <Stack sx={{ width: CHANNEL_COLUMN_WIDTH, alignItems: 'center' }}>
                    {REQUIRED_EMAIL.has(setting) ? (
                      <Tooltip title="Required — critical account email can't be turned off" arrow>
                        {/* span wrapper: a disabled control doesn't emit the hover events Tooltip needs */}
                        <span style={{ display: 'inline-flex' }}>
                          <Switch checked disabled />
                        </span>
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={prefs[setting].email}
                        onChange={() => handleToggle(setting, 'email')}
                      />
                    )}
                  </Stack>
                  <Stack sx={{ width: CHANNEL_COLUMN_WIDTH, alignItems: 'center' }}>
                    {/* VRG-560: SMS is opt-OUT for staff — on unless explicitly turned
                        off — but still never forced/locked, unlike the Email column
                        above, so a REQUIRED_EMAIL category can have its text switched
                        off while its email keeps sending. The `??` is belt-and-braces:
                        `initialPrefs` already resolves an absent key to the category's
                        default, and the fallback must match it or the switch would
                        disagree with the backend. */}
                    <Switch
                      checked={prefs[setting].sms ?? SMS_STAFF_OPT_OUT_DEFAULT}
                      onChange={() => handleToggle(setting, 'sms')}
                    />
                  </Stack>
                </Stack>
              </Stack>
              {index != Object.keys(prefs).length - 1 && <Divider />}
            </Stack>
          ))}
        </Stack>
    </SettingsSection>
  )
}
